import { AUCTION_PLACEHOLDER, FallbackImage } from "../../CommonComponent/FallbackImage";
import { formatUsdWhole } from "../../utils/helper/money";
import {
  ACTION_LABELS,
  AUCTION_ACTIONS,
  BADGE_LABELS,
  actionFor,
  badgeFor,
  formatStart,
} from "../../utils/helper/auctionList";

/**
 * react-data-table-component calls onRowClicked only when the element clicked carries this tag, so
 * every element of a custom cell needs it for a click on it to open the row (#237 AC1).
 */
const ROW_CLICK = { "data-tag": "allowRowEvents" };

/**
 * The Action column. Unlock and Use credit open the pass dialog only, never the row (AC10); a
 * greyed-out Place Bid does nothing, and its wrapper keeps the click from reaching the row (AC12).
 */
const AuctionAction = ({ row, onOpen, onUnlock }) => {
  const action = actionFor(row);
  const label = ACTION_LABELS[action];
  const handle = (callback) => (event) => {
    event.stopPropagation();
    callback(row);
  };

  switch (action) {
    case AUCTION_ACTIONS.UNLOCK:
    case AUCTION_ACTIONS.CREDIT:
      return (
        <button type="button" className="auction-action unlock" onClick={handle(onUnlock)}>
          {label}
        </button>
      );
    case AUCTION_ACTIONS.BID:
      return (
        <button type="button" className="auction-action bid place-bid-btn" onClick={handle(onOpen)}>
          {label}
        </button>
      );
    case AUCTION_ACTIONS.RESULTS:
      return (
        <button type="button" className="auction-action results" onClick={handle(onOpen)}>
          {label}
        </button>
      );
    default:
      return (
        <span className="auction-action-wrap" onClick={(event) => event.stopPropagation()}>
          <button type="button" className="auction-action bid place-bid-btn" disabled>
            {label}
          </button>
        </span>
      );
  }
};

/**
 * The columns of the player's Auctions list (#236), in the ticket's order and none sortable: the
 * order is the server's (Live, then Upcoming by start; Closed most recent first). A click anywhere
 * else in a Live or Upcoming row opens Auction Details (rowTarget, #237).
 *
 * @param {object} options
 * @param {(row: object) => void} options.onOpen Opens the page Place Bid or View Results leads to (actionTarget).
 * @param {(row: object) => void} options.onUnlock Opens the single-auction pass dialog.
 * @returns {Array} react-data-table-component columns.
 */
export const auctionListColumns = ({ onOpen, onUnlock }) => [
  {
    name: "Bid Status",
    width: "124px",
    cell: (row) => {
      const badge = badgeFor(row?.status);
      return (
        <span {...ROW_CLICK} className={`auction-badge ${badge}`}>
          {BADGE_LABELS[badge]}
        </span>
      );
    },
  },
  {
    name: "Auction",
    grow: 3,
    minWidth: "260px",
    cell: (row) => (
      <div {...ROW_CLICK} className="auction-list-auction">
        <FallbackImage {...ROW_CLICK} className="auction-list-thumb" image={row?.auctionImage} placeholder={AUCTION_PLACEHOLDER} />
        <div {...ROW_CLICK}>
          <div {...ROW_CLICK} className="auction-list-name">
            {row?.auctionName}
          </div>
          <div {...ROW_CLICK} className="auction-list-sub">
            {row?.tournamentName} • {row?.sportName}
          </div>
        </div>
      </div>
    ),
  },
  {
    name: "Manager",
    grow: 2,
    wrap: true,
    selector: (row) => row?.managerName,
  },
  {
    name: "Start Date and Time",
    width: "200px",
    selector: (row) => formatStart(row?.startDate),
  },
  {
    name: "Teams",
    width: "90px",
    right: true,
    selector: (row) => row?.teamCount,
  },
  {
    name: "Total Pool",
    width: "120px",
    right: true,
    cell: (row) => (
      <span {...ROW_CLICK} className="auction-list-pool">
        {formatUsdWhole(row?.totalPoolAmount)}
      </span>
    ),
  },
  {
    name: "Action",
    width: "212px",
    right: true,
    // The Action cell never opens the row; its buttons decide.
    ignoreRowClick: true,
    cell: (row) => <AuctionAction row={row} onOpen={onOpen} onUnlock={onUnlock} />,
  },
];
